/**
 * The `hoverElement` relatively to a hoverable element
 * Used by components like DropdownButton and Tooltip.
 */
import clsx from 'clsx';
import debounce from 'lodash.debounce';
import React from 'react';
import { CSSTransition } from 'react-transition-group';

import { getComponentClasses } from '../../utils/app';
// TODO: propose publishing `element-position` to npm
import { positionElementWithAutoAdjustment } from '../../utils/app/elementPosition';
import { DetachableElement } from '../DetachableElement';

export type HoverPosition =
    | 'up-left'
    | 'up-center'
    | 'up-right'
    | 'down-left'
    | 'down-center'
    | 'down-right'
    | 'left-top'
    | 'left-middle'
    | 'left-bottom'
    | 'right-top'
    | 'right-middle'
    | 'right-bottom'
    | 'up-fluid'
    | 'down-fluid';

export interface Props {
    anchorNodeSelector: string;
    alignmentOffset?: number;
    distance?: number;
    hasTransition?: boolean;
    hasAnimation?: boolean;
    hoverPosition?: HoverPosition;
    isActive?: boolean;
    type?: 'tooltip' | 'dropdown' | 'tour';
    onShow?: (el: HTMLElement) => void;
    onHide?: () => void;
    variations?: string[];
    children: React.ReactNode;
}

export class HoverCard extends React.Component<Props, {}> {
    static defaultProps = {
        alignmentOffset: 0,
        distance: 3,
        hasTransition: false,
        hasAnimation: false,
        hoverPosition: 'down-center',
        isActive: false,
        type: 'tooltip',
        onShow: (_el: HTMLElement) => {},
        onHide: () => {},
        variations: [],
        children: '',
    };

    private contentNode: HTMLElement;
    private triangleNode: HTMLElement;
    private cardNode: HTMLElement;
    private debouncedUpdatePositionFunc: (() => void) & ReturnType<typeof debounce>;

    constructor(props: Props) {
        super(props);
        this.updatePosition = this.updatePosition.bind(this);
    }

    componentDidUpdate() {
        if (this.props.isActive && this.props.onShow) {
            this.props.onShow(this.contentNode);
        } else if (this.props.onHide) {
            this.props.onHide();
        }

        this.updatePosition();
    }

    componentWillUnmount() {
        window.removeEventListener('scroll', this.debouncedUpdatePositionFunc, true);
    }

    componentDidMount() {
        this.updatePosition();

        if (!this.debouncedUpdatePositionFunc) {
            this.debouncedUpdatePositionFunc = debounce(this.updatePosition, 50);
        }

        window.addEventListener('scroll', this.debouncedUpdatePositionFunc, true);
    }

    updatePosition() {
        const anchorElement = document.querySelector(this.props.anchorNodeSelector);
        const cardElement = this.cardNode;
        const triangleElement = this.triangleNode;
        const hoverPosition = this.props.hoverPosition || '';
        let cardPosition;

        if (this.props.isActive && anchorElement && this.triangleNode && this.cardNode) {
            cardPosition = this.positionCard(
                cardElement,
                anchorElement,
                triangleElement,
                hoverPosition
            );
            this.positionTriangle(triangleElement, anchorElement, cardElement, cardPosition);
            this.setTransformOrigin(cardElement, triangleElement);
        }
    }

    getPercentageString(part: number, total: number) {
        return total > 0 ? Math.round((part / total) * 100) + '%' : '0%';
    }

    positionCard(
        cardElement: HTMLElement,
        anchorElement: Element,
        triangleElement: HTMLElement,
        position: string
    ) {
        const triangleRect = triangleElement.getBoundingClientRect();
        const distance = this.props.distance || 0;

        return (
            positionElementWithAutoAdjustment(
                cardElement,
                anchorElement,
                position,
                distance + triangleRect.height / 2,
                this.props.alignmentOffset
            ) || ''
        ); // return value might be `null`for `cardPosition` used in `positionTriangle()`
    }

    positionTriangle(
        triangleElement: HTMLElement,
        anchorElement: Element,
        cardElement: HTMLElement,
        cardPosition: string
    ) {
        const positionDirection = cardPosition.split('-').shift();
        const triangleDestinationPosition: { [key: string]: number } = {};
        const anchorRect = anchorElement.getBoundingClientRect();
        const cardRect = cardElement.getBoundingClientRect();
        const triangleRect = triangleElement.getBoundingClientRect();
        const triangleDestinationPageOffsetX =
            anchorRect.left + anchorRect.width / 2 - triangleRect.width / 2;
        const triangleDestinationPageOffsetY =
            anchorRect.top + anchorRect.height / 2 - triangleRect.height / 2;

        if (positionDirection && ['down', 'up'].indexOf(positionDirection) > -1) {
            if (cardRect.width >= anchorRect.width) {
                triangleDestinationPosition['left'] =
                    triangleDestinationPageOffsetX - cardRect.left;
            } else {
                triangleDestinationPosition['left'] = (cardRect.width - triangleRect.width) / 2;
            }
        }

        if (positionDirection && ['left', 'right'].indexOf(positionDirection) > -1) {
            if (cardRect.height >= anchorRect.height) {
                triangleDestinationPosition['top'] = triangleDestinationPageOffsetY - cardRect.top;
            } else {
                triangleDestinationPosition['top'] = (cardRect.height - triangleRect.height) / 2;
            }
        }

        triangleElement.style.top = `${triangleDestinationPosition.top}px`;
        triangleElement.style.left = `${triangleDestinationPosition.left}px`;

        triangleElement.className = '';
        triangleElement.classList.add(
            'hover-card__triangle',
            'hover-card__triangle--' + positionDirection
        );

        return triangleDestinationPosition;
    }

    setTransformOrigin(cardElement: HTMLElement, triangleElement: HTMLElement) {
        const trianglePosition = {
            left: triangleElement.offsetLeft,
            top: triangleElement.offsetTop,
        };
        const cardRect = cardElement.getBoundingClientRect();
        const transformOrigin: { [key: string]: string } = {};

        if (this.props.hasAnimation) {
            transformOrigin['transform-origin-x'] = this.getPercentageString(
                trianglePosition.left,
                cardRect.width
            );
            transformOrigin['transform-origin-y'] = this.getPercentageString(
                trianglePosition.top,
                cardRect.height
            );

            cardElement.style.transformOrigin = `${transformOrigin['transform-origin-x']} ${transformOrigin['transform-origin-y']}`;
        }
    }

    hoverCardContent(hoverCardClassNames: string) {
        return (
            <div
                className={hoverCardClassNames}
                ref={n => {
                    if (n) this.cardNode = n;
                }}
            >
                <div
                    className="hover-card__content"
                    ref={n => {
                        if (n) this.contentNode = n;
                    }}
                >
                    {this.props.children}
                </div>
                <div
                    className="hover-card__triangle"
                    ref={n => {
                        if (n) this.triangleNode = n;
                    }}
                />
            </div>
        );
    }

    render() {
        const hoverCardClassNames = clsx(getComponentClasses('hover-card', this.props.variations), {
            'hover-card--with-transition': this.props.hasTransition,
            'hover-card--with-animation': this.props.hasAnimation,
            'hover-card--tooltip': this.props.type === 'tooltip',
            'hover-card--dropdown': this.props.type === 'dropdown',
            'hover-card--tour': this.props.type === 'tour',
        });

        if (this.props.isActive) {
            if (this.props.hasAnimation || this.props.hasTransition) {
                return (
                    <DetachableElement>
                        <CSSTransition
                            in={true}
                            timeout={{ enter: this.props.hasAnimation ? 300 : 200, exit: 0 }}
                            classNames="hover-card-"
                            appear={true}
                        >
                            {this.hoverCardContent(hoverCardClassNames)}
                        </CSSTransition>
                    </DetachableElement>
                );
            } else {
                return (
                    <DetachableElement>
                        {this.hoverCardContent(hoverCardClassNames)}
                    </DetachableElement>
                );
            }
        } else {
            return null;
        }
    }
}

export default HoverCard;
